import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "motion/react";
import { cn } from "@/lib/utils";
import {
  ClaudeIcon,
  ChatGptIcon,
  GoogleGeminiIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";

// ─── Shared easing ────────────────────────────────────────────────
const ease = [0.25, 0.46, 0.45, 0.94] as const;

// ─── FeatureCard with staggered text reveal ───────────────────────
interface FeatureCardProps {
  tag: string;
  title: string;
  description: string;
  className?: string;
  children?: React.ReactNode;
}

function FeatureCard({
  tag,
  title,
  description,
  className,
  children,
}: FeatureCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <div
      ref={ref}
      className={cn(
        "overflow-hidden rounded-2xl border border-black/5 bg-card p-8 shadow-sm dark:border-white/10 dark:bg-white/5",
        className,
      )}
    >
      <motion.span
        className="text-brand inline-block text-xs font-semibold uppercase tracking-widest"
        initial={{ opacity: 0, y: 8 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.4, ease }}
      >
        {tag}
      </motion.span>

      <motion.h3
        className="text-foreground mt-3 text-[22px] font-medium leading-[1.3]"
        initial={{ opacity: 0, y: 12 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.45, ease, delay: 0.1 }}
      >
        {title}
      </motion.h3>

      <motion.p
        className="text-muted-foreground mt-3 text-[15px] leading-[1.6]"
        initial={{ opacity: 0, y: 12 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.45, ease, delay: 0.2 }}
      >
        {description}
      </motion.p>

      <motion.div
        className="mt-8"
        initial={{ opacity: 0, y: 16 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, ease, delay: 0.35 }}
      >
        {children ?? (
          <div className="h-48 rounded-xl bg-black/5 dark:bg-white/5" />
        )}
      </motion.div>
    </div>
  );
}

// ─── Illustration 1 — Animated Node Graph (loops) ─────────────────
//
// A macOS cursor glides across the canvas, clicking to place nodes.
// After the graph is built the whole thing resets and loops.
// ───────────────────────────────────────────────────────────────────

function NodeGraphIllustration() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [step, setStep] = useState(0);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const schedule: [number, number][] = [
      [1, 600], // click  → Node 1
      [2, 1300], // arrow 1 + cursor glides
      [3, 1900], // click  → Node 2
      [4, 2600], // arrow 2 + cursor glides
      [5, 3200], // click  → Node 3
      [6, 4200], // cursor fades out
    ];
    const timers: ReturnType<typeof setTimeout>[] = schedule.map(([s, delay]) =>
      setTimeout(() => setStep(s), delay),
    );
    // Loop: reset → remount via cycle key
    timers.push(
      setTimeout(() => {
        setStep(0);
        setCycle((c) => c + 1);
      }, 9000),
    );
    return () => timers.forEach(clearTimeout);
  }, [isInView, cycle]);

  const cursorLeft = step <= 1 ? 16 : step <= 3 ? 48 : 80;

  return (
    <div
      ref={ref}
      className="relative h-52 overflow-hidden rounded-xl bg-black/[0.03] dark:bg-white/[0.03]"
    >
      {/* Dot grid background (static — outside cycle key) */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "radial-gradient(circle, currentColor 0.5px, transparent 0.5px)",
          backgroundSize: "16px 16px",
        }}
      />

      {/* Cycle key resets all motion initial states on loop */}
      <div key={cycle}>
        {/* ── macOS Cursor ───────────────────────────────────────── */}
        <motion.div
          className="pointer-events-none absolute z-20"
          initial={{ opacity: 0, left: "-2%", top: "66%" }}
          animate={{
            opacity: isInView && step < 6 ? 1 : 0,
            left: `${cursorLeft}%`,
            top: "56%",
          }}
          transition={{
            left: { duration: 0.45, ease: "easeInOut" },
            top: { duration: 0.45, ease: "easeInOut" },
            opacity: { duration: 0.3 },
          }}
        >
          <MacCursor />
        </motion.div>

        {/* ── Click ripple ─────────────────────────────────────── */}
        {[1, 3, 5].includes(step) && (
          <motion.div
            key={`ripple-${step}`}
            className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-brand/60"
            style={{
              left: `${step === 1 ? 16 : step === 3 ? 48 : 80}%`,
              top: "50%",
            }}
            initial={{ width: 0, height: 0, opacity: 0.7 }}
            animate={{ width: 36, height: 36, opacity: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
          />
        )}

        {/* ── Nodes & arrows ───────────────────────────────────── */}
        <div className="absolute inset-0 flex items-center justify-center gap-3 px-4 sm:gap-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={step >= 1 ? { opacity: 1, scale: 1 } : {}}
            transition={{ type: "spring", stiffness: 350, damping: 25 }}
          >
            <NodePill label="Text Gen" dotColor="bg-blue-400" active />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={step >= 2 ? { opacity: 1, scaleX: 1 } : {}}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={{ transformOrigin: "left center" }}
          >
            <Arrow />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={step >= 3 ? { opacity: 1, scale: 1 } : {}}
            transition={{ type: "spring", stiffness: 350, damping: 25 }}
          >
            <NodePill label="Image" dotColor="bg-brand" active />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={step >= 4 ? { opacity: 1, scaleX: 1 } : {}}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={{ transformOrigin: "left center" }}
          >
            <Arrow />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={step >= 5 ? { opacity: 1, scale: 1 } : {}}
            transition={{ type: "spring", stiffness: 350, damping: 25 }}
          >
            <NodePill label="Video" dotColor="bg-emerald-400" active />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

// macOS default cursor — black arrow, white border
function MacCursor() {
  return (
    <svg
      width="17"
      height="23"
      viewBox="0 0 17 23"
      fill="none"
      className="drop-shadow-[0_1px_3px_rgba(0,0,0,0.25)]"
    >
      <path
        d="M1.5 1L1.5 18.5L5.93 13.43L9.78 21.78L12.28 20.53L8.43 12.18L14.28 12.18L1.5 1Z"
        fill="black"
        stroke="white"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NodePill({
  label,
  dotColor,
  active,
}: {
  label: string;
  dotColor: string;
  active?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[10px] font-medium shadow-sm",
        active
          ? "border-brand/30 bg-brand-muted text-foreground shadow-brand/5"
          : "border-border bg-card text-foreground",
      )}
    >
      <div
        className={cn(
          "size-2 rounded-full",
          dotColor,
          active && "animate-pulse",
        )}
      />
      <span className="whitespace-nowrap">{label}</span>
    </div>
  );
}

function Arrow() {
  return (
    <div className="flex items-center">
      <div className="bg-border h-px w-6" />
      <div className="size-0 border-y-[3px] border-l-[5px] border-y-transparent border-l-brand/50" />
    </div>
  );
}

// ─── Illustration 2 — Model Chain Progress ────────────────────────
function ModelChainIllustration() {
  return (
    <div className="flex h-52 items-center justify-center rounded-xl bg-black/[0.03] px-6 dark:bg-white/[0.03]">
      <div className="flex items-center gap-4 sm:gap-6">
        {/* Step 1 — done */}
        <StepModel model="claude" />
        <Connector />

        {/* Step 3 — active */}
        <StepModel model="gpt" />

        {/* Dashed connector */}
        <div className="border-muted-foreground/25 h-0 w-6 border-t-2 border-dashed sm:w-10" />

        <div className="bg-brand text-brand-foreground relative flex size-11 items-center justify-center rounded-full shadow-lg shadow-brand/30">
          <HugeiconsIcon icon={GoogleGeminiIcon} className="size-5 sm:size-6" />
          <span className="border-brand absolute inset-0 animate-ping rounded-full border-2 opacity-25" />
        </div>
      </div>
    </div>
  );
}

function StepModel({ model }: { model: "claude" | "gpt" | "gemini" }) {
  return (
    <div className="bg-brand text-brand-foreground flex size-11 items-center justify-center rounded-full shadow-md shadow-brand/20">
      <HugeiconsIcon
        icon={
          model === "claude"
            ? ClaudeIcon
            : model === "gpt"
              ? ChatGptIcon
              : GoogleGeminiIcon
        }
        className="size-5 sm:size-6"
      />
    </div>
  );
}

function Connector() {
  return <div className="h-0.5 w-6 bg-brand/50 sm:w-10" />;
}

// ─── Illustration 3 — Chat Mockup (Live/Animated) ────────────────
function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-0.5">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="size-1.5 animate-bounce rounded-full bg-muted-foreground/40"
          style={{ animationDelay: `${i * 0.15}s`, animationDuration: "0.9s" }}
        />
      ))}
    </div>
  );
}

interface ChatScenario {
  userMsg: string;
  buildingMsg: string;
  checks: [string, string, string];
  readyMsg: string;
}

const SCENARIOS: ChatScenario[] = [
  {
    userMsg: "Create a video pipeline from a text prompt",
    buildingMsg: "Building your pipeline...",
    checks: ["Added Text Input node", "Added LLM Text node", "Added Video Gen node"],
    readyMsg: "Your pipeline is ready!",
  },
  {
    userMsg: "Generate blog posts with matching images",
    buildingMsg: "Setting up your workflow...",
    checks: ["Added Text Input node", "Added LLM Text node", "Added Image Gen node"],
    readyMsg: "Your pipeline is ready!",
  },
  {
    userMsg: "Turn my scripts into audio narrations",
    buildingMsg: "Assembling your pipeline...",
    checks: ["Added File Input node", "Added LLM Text node", "Added Audio TTS node"],
    readyMsg: "Your pipeline is ready!",
  },
  {
    userMsg: "Clean up and upscale my product photos",
    buildingMsg: "Building your pipeline...",
    checks: ["Added Image Input node", "Added Background Remover", "Added Image Upscaler"],
    readyMsg: "Your pipeline is ready!",
  },
];

function ChatIllustration() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [step, setStep] = useState(-1);
  const [cycle, setCycle] = useState(0);
  const [typedChars, setTypedChars] = useState(0);
  const [typedBuildingChars, setTypedBuildingChars] = useState(0);
  const [typedReadyChars, setTypedReadyChars] = useState(0);

  const scenario = SCENARIOS[cycle % SCENARIOS.length];

  useEffect(() => {
    if (!isInView) return;

    const s = SCENARIOS[cycle % SCENARIOS.length];

    setTypedChars(0);
    setTypedBuildingChars(0);
    setTypedReadyChars(0);
    setStep(-1);

    const allTimers: ReturnType<typeof setTimeout>[] = [];
    let typeInterval: ReturnType<typeof setInterval> | null = null;
    let buildingInterval: ReturnType<typeof setInterval> | null = null;
    let readyInterval: ReturnType<typeof setInterval> | null = null;

    // User message types in after a brief pause
    allTimers.push(
      setTimeout(() => {
        let chars = 0;
        typeInterval = setInterval(
          () => {
            chars++;
            setTypedChars(chars);
            if (chars >= s.userMsg.length && typeInterval) {
              clearInterval(typeInterval);
              typeInterval = null;
            }
          },
          Math.round(680 / s.userMsg.length),
        );
      }, 300),
    );

    // Sequence — timed from loop start
    const schedule: [number, number][] = [
      [1, 1100], // typing done → hide cursor
      [2, 1550], // AI bubble + typing dots
      [3, 2500], // building message
      [4, 3100], // check 1
      [5, 3700], // check 2
      [6, 4300], // check 3
      [7, 5000], // ready message
    ];

    schedule.forEach(([step, delay]) => {
      allTimers.push(setTimeout(() => setStep(step), delay));
    });

    // Building message types in at step 3 (t=2500)
    allTimers.push(
      setTimeout(() => {
        let chars = 0;
        buildingInterval = setInterval(
          () => {
            chars++;
            setTypedBuildingChars(chars);
            if (chars >= s.buildingMsg.length && buildingInterval) {
              clearInterval(buildingInterval);
              buildingInterval = null;
            }
          },
          Math.round(380 / s.buildingMsg.length),
        );
      }, 2500),
    );

    // Ready message types in at step 7 (t=5000)
    allTimers.push(
      setTimeout(() => {
        let chars = 0;
        readyInterval = setInterval(
          () => {
            chars++;
            setTypedReadyChars(chars);
            if (chars >= s.readyMsg.length && readyInterval) {
              clearInterval(readyInterval);
              readyInterval = null;
            }
          },
          Math.round(350 / s.readyMsg.length),
        );
      }, 5000),
    );

    // Loop
    allTimers.push(
      setTimeout(() => {
        setStep(-1);
        setTypedChars(0);
        setTypedBuildingChars(0);
        setTypedReadyChars(0);
        setCycle((c) => c + 1);
      }, 9000),
    );

    return () => {
      allTimers.forEach(clearTimeout);
      if (typeInterval) clearInterval(typeInterval);
      if (buildingInterval) clearInterval(buildingInterval);
      if (readyInterval) clearInterval(readyInterval);
    };
  }, [isInView, cycle]);

  return (
    <div
      ref={ref}
      className="mx-auto flex h-56 max-w-lg flex-col justify-start gap-3 rounded-xl bg-black/[0.03] px-5 py-5 dark:bg-white/[0.03] sm:px-8"
    >
      <div className="flex flex-col gap-3">
        {/* User message — appears instantly, no animation */}
        {/* {typedChars > 0 && ( */}
        <div className="flex justify-end">
          <div className="max-w-[75%] rounded-2xl rounded-br-md bg-brand/10 px-4 py-2.5">
            <p className="text-foreground/80 text-left text-[11px] leading-relaxed sm:text-xs">
              <span>{scenario.userMsg.slice(0, typedChars)}</span>
              <span className="select-none opacity-0">
                {scenario.userMsg.slice(typedChars)}
              </span>
            </p>
          </div>
        </div>
        {/* )} */}

        {/* AI bubble — fades in, grows downward */}
        {step >= 2 && (
          <div className="flex max-w-[85%] items-start gap-2.5">
            <motion.div
              className="bg-brand mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full"
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 22 }}
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 2L9.5 6.5L14 8L9.5 9.5L8 14L6.5 9.5L2 8L6.5 6.5L8 2Z"
                  fill="white"
                />
              </svg>
            </motion.div>

            <motion.div
              className="rounded-2xl rounded-bl-md bg-muted/50 px-4 py-2.5 dark:bg-white/[0.04]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              {step === 2 ? (
                <TypingDots />
              ) : (
                <div className="space-y-1.5">
                  <p className="text-foreground/70 mb-2 text-left text-[11px] leading-relaxed sm:text-xs">
                    {scenario.buildingMsg.slice(0, typedBuildingChars)}
                  </p>
                  {step >= 4 && <ChatCheck label={scenario.checks[0]} />}
                  {step >= 5 && <ChatCheck label={scenario.checks[1]} />}
                  {step >= 6 && <ChatCheck label={scenario.checks[2]} />}
                  {step >= 7 && (
                    <p className="text-foreground/70 text-left text-[11px] leading-relaxed sm:text-xs">
                      {scenario.readyMsg.slice(0, typedReadyChars)}
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChatCheck({ label }: { label: string }) {
  return (
    <motion.div
      className="flex items-center gap-1.5 text-[10px] sm:text-[11px]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
    >
      <motion.svg
        width="12"
        height="12"
        viewBox="0 0 16 16"
        fill="none"
        className="text-brand shrink-0"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 600,
          damping: 18,
          delay: 0.06,
        }}
      >
        <path
          d="M3.5 8L6.5 11L12.5 5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </motion.svg>
      <span className="text-foreground/60">{label}</span>
    </motion.div>
  );
}

// ─── Section ──────────────────────────────────────────────────────
export function FeaturesGrid() {
  return (
    <section className="py-24">
      <div className="mx-auto max-w-5xl px-6">
        {/* Section heading */}
        <div className="text-center">
          <h2 className="text-foreground text-4xl font-display tracking-normal sm:text-5xl">
            Everything you need to build
            <br />
            AI content workflows
          </h2>
          <p className="text-muted-foreground mx-auto mt-6 max-w-[540px] text-base leading-relaxed sm:text-lg">
            A complete visual toolkit for chaining AI models into automated
            content pipelines.
          </p>
        </div>

        {/* 2 side-by-side cards */}
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          <FeatureCard
            tag="Visual Builder"
            title="Design AI workflows visually"
            description="Build complex content pipelines on an intuitive node-based canvas. Connect AI models visually, configure parameters, and see your workflow take shape in real time."
          >
            <NodeGraphIllustration />
          </FeatureCard>
          <FeatureCard
            tag="Model Chaining"
            title="Connect AI Models seamlessly"
            description="Chain text generation, image creation, video synthesis, audio production, and processing nodes together. Each output flows naturally into the next step."
          >
            <ModelChainIllustration />
          </FeatureCard>
        </div>

        {/* Full-width card */}
        <div className="mt-6">
          <FeatureCard
            tag="AI Assistant"
            title="Build pipelines through conversation"
            description="Describe what you want to create in plain English. The AI assistant builds your pipeline, connects nodes, and configures models. All from a simple chat interface."
            className="text-center"
          >
            <ChatIllustration />
          </FeatureCard>
        </div>
      </div>
    </section>
  );
}
